import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient'; // Your Supabase client
import {
  Container, Box, Typography, Button, CircularProgress, Alert, Paper, Grid
} from '@mui/material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin; // Or your functions invoke URL prefix

function UsageChoice() {
  const navigate = useNavigate();
  const [loadingChoice, setLoadingChoice] = useState(null); // null, 'company', or 'personal'
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Check if user already has a company_id on mount - might be redundant if App.js handles this
  // but can be a good failsafe or for direct navigation to this page.
  useEffect(() => {
    const checkUserCompany = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        if (profileError && profileError.code !== 'PGRST116') {
          setError('Could not verify user profile. Please try again.');
          return;
        }
        if (profile && profile.company_id) {
          // User already has a company, should not be on this page normally
          // Navigate to dashboard or appropriate next step
          navigate('/fileupload'); // Adjust as needed
        }
      }
    };
    checkUserCompany();
  }, [navigate]);

  const handleChoice = async (choiceType) => { // Renamed parameter to choiceType
    setLoadingChoice(choiceType); // Set which choice is loading
    setError('');
    setMessage('');

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('User session not found. Please log in.');
      }
      const token = session.access_token;
      const apikey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (choiceType === 'company') {
        navigate('/onboarding/create-company');
      } else if (choiceType === 'personal') {
        // Call the backend Edge Function to set up personal workspace
        // Ensure this path matches your deployed Edge Function
        const response = await fetch(`${BACKEND_URL}/functions/v1/onboarding-setup-personal-workspace`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'apikey': apikey,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to set up personal workspace.');
        }
        
        setMessage(result.message || 'Personal workspace set up successfully!');
        // Decide next step: create team or go to dashboard
        // For simplicity, let's go to dashboard (or fileupload)
        setTimeout(() => navigate('/fileupload'), 1500); // Navigate after a short delay
      }
    } catch (err) {
      console.error('Error handling usage choice:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoadingChoice(null); // Reset loading choice
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          How will you be using this account?
        </Typography>

        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{message}</Alert>}

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => handleChoice('company')}
              disabled={loadingChoice !== null} // Disable if any choice is loading
              sx={{ padding: '15px', minHeight: '80px' }}
            >
              {loadingChoice === 'company' ? <CircularProgress size={24} /> : 'For my Company / Organization'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => handleChoice('personal')}
              disabled={loadingChoice !== null} // Disable if any choice is loading
              sx={{ padding: '15px', minHeight: '80px' }}
            >
              {loadingChoice === 'personal' ? <CircularProgress size={24} /> : 'For Personal Use'}
            </Button>
          </Grid>
        </Grid>
        
        {loadingChoice && ( // General loading indicator if needed, or rely on button-specific
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
                <CircularProgress />
                <Typography sx={{ml: 2}}>Processing...</Typography>
            </Box>
        )}
      </Paper>
    </Container>
  );
}

export default UsageChoice; 